import { useCallback, useRef } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { Modal, ModalHeader, ModalTitle, ModalBody } from '../primitives/Modal';
import { FormView } from '../form/FormView';
import type { AnyFieldDef } from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';

interface ResourceFormDialogProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  /** Prefill from an action `context`. Sent even when untouched. */
  initialValues?: Partial<T>;
  onClose: () => void;
}

/**
 * CREATE-only Modal shell around `FormView` (#1797).
 *
 * Editing an existing record no longer opens a dialog at all: it is an in-place
 * mode switch inside whatever surface already shows the record. Create is
 * different in kind — there is no record on screen to switch — so it keeps a
 * Modal, but the Modal is now just a container: dirty tracking, validation,
 * payload construction and focus all live in the one `FormView` engine.
 */
export function ResourceFormDialog<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  initialValues,
  onClose,
}: ResourceFormDialogProps<T, F>) {
  const { t, confirm } = useResourceKit();
  const dirtyRef = useRef(false);

  const defLabel = t(bundle.host.label, { defaultValue: bundle.host.label });
  const config = bundle.host.formConfig;
  const title = config?.createTitle
    ? t(config.createTitle, { defaultValue: config.createTitle })
    : t('rk.resource.createTitle', { name: defLabel });
  const size = config?.maxWidth === 'sm' ? 'sm' : config?.maxWidth === 'lg' ? 'lg' : 'md';

  // Closing via the backdrop / the header X bypasses FormView's own Cancel,
  // so the discard guard is re-asserted here rather than silently dropped.
  const requestClose = useCallback(() => {
    if (!dirtyRef.current) {
      onClose();
      return;
    }
    void confirm({
      title: t('rk.resource.discardTitle'),
      message: t('rk.resource.discardMessage'),
      intent: 'warning',
      confirmText: t('rk.resource.discardConfirm'),
      cancelText: t('rk.resource.keepEditing'),
    }).then((ok) => {
      if (ok) onClose();
    });
  }, [confirm, onClose, t]);

  return (
    <Modal isOpen onClose={requestClose} size={size}>
      <ModalHeader onClose={requestClose}>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <FormView
          bundle={bundle}
          columns={[]}
          record={null}
          intent="create"
          initialValues={initialValues}
          canEdit
          embedded
          onCloseCreate={onClose}
          onDirtyChange={(dirty) => {
            dirtyRef.current = dirty;
          }}
        />
      </ModalBody>
    </Modal>
  );
}
